import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { getAppVersion } from '@/utils/version';
import type { AppRouter, InitClientArgs } from '@ts-rest/core';
import { initClient, tsRestFetchApi } from '@ts-rest/core';
import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import Cookies from 'universal-cookie';

export type { ClientInferResponseBody as ResponseBody } from '@ts-rest/core';

/**
 * Custom error class for API errors with the full response
 */
export class APIError extends Error {
  response: Awaited<ReturnType<typeof tsRestFetchApi>>;

  constructor(message: string, response: any) {
    super(message);
    this.name = 'APIError';
    this.response = response;
  }
}

/**
 * Type guard to check if an error is an instance of APIError.
 *
 * Usage:
 * ```ts
 * if (isAPIError<ClientInferResponseBody<typeof testContract.getUser, 200>>(error)) {
 *   // Handle APIError with specific response body type
 * }
 * ```
 */
export function isAPIError<ResponseBody = unknown>(
  error: unknown,
): error is APIError & {
  response: Awaited<ReturnType<typeof tsRestFetchApi>> & {
    body: ResponseBody;
  };
} {
  return error instanceof APIError;
}

/**
 * Creates a basic http api client and a tanstack react query client
 * for the given ts-rest contract and options.
 *
 * Example usage:
 * ```ts
 * import { createApi } from '@/services/api/ts-rest';
 * import { appRouter } from '@/services/api/contract';
 * const { api, query } = createApi(appRouter, {
 *  baseUrl: '/api',
 * });
 *
 * const user = await api.getUser({ ... });
 *
 * or
 *
 * const { data: user } = query.getUser.useQuery({ ... });
 */
export function createApi<T extends AppRouter, TClientArgs extends InitClientArgs>(contract: T, options: TClientArgs) {
  /**
   * The customApiHandler function is used to create a custom API handler
   * that can be used with ts-rest. It handles the API requests and responses,
   * including setting the appropriate headers
   */
  const baseApiHandler: InitClientArgs['api'] = async (args) => {
    const cookies = new Cookies();
    const accessToken = cookies.get('_access');
    const apiUrl = getApiUrl();

    const response = await tsRestFetchApi({
      ...args,
      path: `${apiUrl}${args.path}`,
      fetchOptions: {
        credentials: 'include',
      },
      headers: {
        ...args.headers,
        'x-ui-version': getAppVersion(),
        ...(accessToken == null || accessToken === '' ? {} : { Authorization: `Bearer ${accessToken}` }),
      },
    });

    // We throw an error if the response status is 400 or higher to make our control
    // flows simple (i.e. try/catch around the api calls).
    if (response.status >= 400) {
      const errorMessage =
        typeof response.body === 'object' && response.body !== null && 'message' in response.body
          ? String(response.body.message)
          : `An unexpected error occured with HTTP code ${response.status}`;
      throw new APIError(errorMessage, response);
    }
    return response;
  };

  const api = modifyApiToBeHappyPath(
    initClient(contract, {
      ...options,
      api: baseApiHandler,
    }),
  );

  const query = modifyTsrToAutoGenerateQueryKey(
    initTsrReactQuery(contract, {
      ...options,
      api: baseApiHandler,
    }),
  );

  return {
    api,
    query,
  };
}

/**
 * Generates a query key for the given method name and query data.
 */
export function getQueryKey(methodName: string, queryData: any) {
  return [methodName, queryData];
}

/**
 * Auto-generate a query key for TSR React Query methods.
 */
export function autoGenerateKey() {
  // the typescript type require a queryKey to be passed in, but we don't
  // want to require since auto-generate it if none is passed in.
  //
  // so we create this no-op function that returns undefined so that
  // typescript is happy but our default behavior of auto-generating a query key
  // is still used.

  return undefined as unknown as ReturnType<typeof getQueryKey>;
}

/**
 * Sets the params for the request, including the account ID and workspace ID
 */
export function contextualParams<T extends Record<string, any>>(params: T = {} as T) {
  const accountId = window.__hydra__?.account?.id;
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  return {
    accountId,
    workspaceId,
    ...params,
  };
}

/**
 * gets the base URL for the API based on the current account region
 * and environment.
 */
function getApiUrl() {
  // Default to US for auth and calls made before an account is known
  const region = window.__hydra__?.account?.dc ?? 'us';

  if (__CIO_ENV__ === 'production') {
    return {
      us: 'https://us.fly.customer.io',
      eu: 'https://eu.fly.customer.io',
    }[region];
  } else if (__CIO_ENV__ === 'standalone') {
    return {
      us: 'https://us.fly-standalone.customer.io',
      eu: 'https://us.fly-standalone.customer.io',
    }[region];
  } else {
    return '';
  }
}

/**
 * Modifies the tsr object to automatically generate query keys
 *
 * This makes it harder for our developers to pass in inconsistent
 * query keys and have extra calls to the API.
 *
 */
function modifyTsrToAutoGenerateQueryKey<T>(tsr: T): T {
  for (const [key, endpoint] of Object.entries(tsr as Record<string, unknown>)) {
    if (typeof endpoint !== 'object' || endpoint === null) {
      continue;
    }

    ['useQuery', 'usePrefetchQuery', 'useQueries', 'useSuspenseQueries', 'useSuspenseQuery'].forEach((method) => {
      if (!(method in endpoint)) {
        return;
      }

      const fn = (endpoint as Record<string, unknown>)[method];

      if (typeof fn === 'function') {
        const originalFn = fn.bind(endpoint);
        (endpoint as Record<string, unknown>)[method] = (args: any) => {
          const queryKey = args.queryKey ?? getQueryKey(key, args.queryData);
          return originalFn({ ...args, queryKey });
        };
      }
    });
  }

  return tsr;
}

type Extract200Response<T> = T extends Promise<infer U>
  ? U extends { status: 200; body: infer B }
    ? Promise<B>
    : never
  : T;

type TransformFunctions<T> = {
  [K in keyof T]: T[K] extends (...args: infer A) => infer R ? (...args: A) => Extract200Response<R> : T[K];
};

/**
 * Modifies the API object to return the 200 response body directly since
 * we throw an error if the response status is not 2xx.
 */
export function modifyApiToBeHappyPath<T extends Record<string, any>>(obj: T): TransformFunctions<T> {
  const result: any = {};

  for (const key in obj) {
    const val = obj[key];
    if (typeof val === 'function') {
      result[key] = async (...args: any[]) => {
        const res = await val(...args);

        return res.body;
      };
    } else {
      result[key] = val;
    }
  }

  return result;
}
